import { tokenManager } from '../utils/api';
import { USER_URL } from '../config/environment';
import axios from 'axios';

// ============================
// SFTP CONFIGURATIONS
// ============================

/**
 * Get all SFTP configurations for an account
 * @param {string} accountId - The account ID
 * @returns {Promise} API response
 */
export const getAllConfigurations = async (accountId) => {
  const response = await axios.get(`${USER_URL}/api/account-configs/${accountId}/sftp-config`, {
    headers: { Authorization: `Bearer ${tokenManager.getAccessToken()}` }
  });
  return response.data;
};

/**
 * Get SFTP configurations by type (import/export)
 * @param {string} accountId - The account ID
 * @param {string} configType - 'import' or 'export'
 * @returns {Promise} API response
 */
export const getConfigurationsByType = async (accountId, configType) => {
  const response = await axios.get(`${USER_URL}/api/account-configs/${accountId}/sftp-config/by-type`, {
    params: { configType },
    headers: { Authorization: `Bearer ${tokenManager.getAccessToken()}` }
  });
  return response.data;
};

/**
 * Get SFTP configuration by ID
 * @param {string} accountId - The account ID
 * @param {string} configId - The configuration ID
 * @returns {Promise} API response
 */
export const getConfigurationById = async (accountId, configId) => {
  const response = await axios.get(`${USER_URL}/api/account-configs/${accountId}/sftp-config/${configId}`, {
    headers: { Authorization: `Bearer ${tokenManager.getAccessToken()}` }
  });
  return response.data;
};

/**
 * Create a new SFTP configuration
 * @param {string} accountId - The account ID
 * @param {Object} data - Configuration data
 * @returns {Promise} API response
 */
export const createConfiguration = async (accountId, data) => {
  const response = await axios.post(`${USER_URL}/api/account-configs/${accountId}/sftp-config`, data, {
    headers: { Authorization: `Bearer ${tokenManager.getAccessToken()}` }
  });
  return response.data;
};

/**
 * Update an existing SFTP configuration
 * @param {string} accountId - The account ID
 * @param {string} configId - The configuration ID
 * @param {Object} data - Updated configuration data
 * @returns {Promise} API response
 */
export const updateConfiguration = async (accountId, configId, data) => {
  const response = await axios.put(`${USER_URL}/api/account-configs/${accountId}/sftp-config/${configId}`, data, {
    headers: { Authorization: `Bearer ${tokenManager.getAccessToken()}` }
  });
  return response.data;
};

/**
 * Delete an SFTP configuration
 * @param {string} accountId - The account ID
 * @param {string} configId - The configuration ID
 * @returns {Promise} API response
 */
export const deleteConfiguration = async (accountId, configId) => {
  const response = await axios.delete(`${USER_URL}/api/account-configs/${accountId}/sftp-config/${configId}`, {
    headers: { Authorization: `Bearer ${tokenManager.getAccessToken()}` }
  });
  return response.data;
};

/**
 * Test SFTP connection
 * @param {string} accountId - The account ID
 * @param {string} configId - The configuration ID
 * @returns {Promise} API response
 */
export const testConnection = async (accountId, configId) => {
  const response = await axios.post(`${USER_URL}/api/account-configs/${accountId}/sftp-config/${configId}/test`, {}, {
    headers: { Authorization: `Bearer ${tokenManager.getAccessToken()}` }
  });
  return response.data;
};

// ============================
// EXPORT SCHEDULES
// ============================

/**
 * Get all export schedules for an account
 * GET /api/sftp/schedules?accountId={accountId}
 * @param {string} accountId - The account ID
 * @returns {Promise} API response
 */
export const getAllSchedules = async (accountId) => {
  const response = await axios.get(`${USER_URL}/api/account-configs/${accountId}/sftp-config/schedules`, {
    // params: { accountId },
    headers: { Authorization: `Bearer ${tokenManager.getAccessToken()}` }
  });
  return response.data;
};

/**
 * Get export schedule by ID
 * GET /api/sftp/schedules/{scheduleId}?accountId={accountId}
 * @param {string} accountId - The account ID
 * @param {string} scheduleId - The schedule ID
 * @returns {Promise} API response
 */
export const getScheduleById = async (accountId, scheduleId) => {
  const response = await axios.get(`${USER_URL}/api/account-configs/${accountId}/sftp-config/schedules/${scheduleId}`, {
    headers: { Authorization: `Bearer ${tokenManager.getAccessToken()}` }
  });
  return response.data;
};

/**
 * Create a new export schedule
 * POST /api/account-configs/{accountId}/sftp-config/schedules
 * Supports: CLICK_DATA, URL_SHORTENING, CREDIT_HISTORY, CREDIT_CONSUMPTION
 * Frequencies: DAILY, WEEKLY, MONTHLY, YEARLY, ONCE (for one-time execution)
 * @param {string} accountId - The account ID
 * @param {Object} data - Schedule data with reportType, frequency, scheduledTime, filters
 * @returns {Promise} API response
 */
export const createSchedule = async (accountId, data) => {
  const response = await axios.post(`${USER_URL}/api/account-configs/${accountId}/sftp-config/schedules`, data, {
    headers: { 
      Authorization: `Bearer ${tokenManager.getAccessToken()}`,
      'Content-Type': 'application/json'
    }
  });
  return response.data;
};

/**
 * Update an existing export schedule
 * PUT /api/sftp/schedules/{scheduleId}?accountId={accountId}
 * @param {string} accountId - The account ID
 * @param {string} scheduleId - The schedule ID
 * @param {Object} data - Updated schedule data
 * @returns {Promise} API response
 */
export const updateSchedule = async (accountId, scheduleId, data) => {
  const response = await axios.put(`${USER_URL}/api/account-configs/${accountId}/sftp-config/schedules/${scheduleId}`, data, {
    headers: { 
      Authorization: `Bearer ${tokenManager.getAccessToken()}`,
      'Content-Type': 'application/json'
    }
  });
  return response.data;
};

/**
 * Toggle schedule enable/disable status
 * PATCH /api/sftp/schedules/{scheduleId}/toggle?accountId={accountId}
 * @param {string} accountId - The account ID
 * @param {string} scheduleId - The schedule ID
 * @returns {Promise} API response
 */
export const toggleSchedule = async (accountId, scheduleId) => {
  const response = await axios.patch(`${USER_URL}/api/account-configs/${accountId}/sftp-config/schedules/${scheduleId}/toggle`, {}, {
    headers: { Authorization: `Bearer ${tokenManager.getAccessToken()}` }
  });
  return response.data;
};

/**
 * Execute a schedule immediately
 * POST /api/sftp/schedules/{scheduleId}/execute?accountId={accountId}
 * @param {string} accountId - The account ID
 * @param {string} scheduleId - The schedule ID
 * @returns {Promise} API response
 */
export const executeScheduleNow = async (accountId, scheduleId) => {
  const response = await axios.post(`${USER_URL}/api/account-configs/${accountId}/sftp-config/schedules/${scheduleId}/run-now`, {}, {
    headers: { Authorization: `Bearer ${tokenManager.getAccessToken()}` }
  });
  return response.data;
};

/**
 * Delete an export schedule
 * DELETE /api/sftp/schedules/{scheduleId}?accountId={accountId}
 * @param {string} accountId - The account ID
 * @param {string} scheduleId - The schedule ID
 * @returns {Promise} API response
 */
export const deleteSchedule = async (accountId, scheduleId) => {
  const response = await axios.delete(`${USER_URL}/api/account-configs/${accountId}/sftp-config/schedules/${scheduleId}`, {
    params: { accountId },
    headers: { Authorization: `Bearer ${tokenManager.getAccessToken()}` }
  });
  return response.data;
};

// ============================
// EXPORT HISTORY & STATISTICS
// ============================

/**
 * Get export history with optional filters
 * GET /api/sftp/export-history?accountId={accountId}&status={status}&page={page}&size={size}
 * @param {string} accountId - The account ID
 * @param {Object} params - Query parameters (status, page, size)
 * @returns {Promise} API response
 */
export const getExportHistory = async (accountId, params = {}) => {
  const { status, page = 0, size = 10 } = params;
  const queryParams = {
    accountId,
    page,
    size
  };
  
  if (status) {
    queryParams.status = status;
  }
  
  const response = await axios.get(
    `${USER_URL}/api/sftp/export-history`,
    {
      params: queryParams,
      headers: { Authorization: `Bearer ${tokenManager.getAccessToken()}` }
    }
  );
  return response.data;
};

/**
 * Get export history for a specific schedule
 * GET /api/sftp/schedules/{scheduleId}/history?accountId={accountId}&page={page}&size={size}
 * @param {string} accountId - The account ID
 * @param {string} scheduleId - The schedule ID
 * @param {Object} params - Query parameters (page, size)
 * @returns {Promise} API response
 */
export const getScheduleExportHistory = async (accountId, scheduleId, params = {}) => {
  const { page = 0, size = 10 } = params;
  
  const response = await axios.get(

    // {{user_url}}/api/account-configs/{{accountId}}/sftp-config/export-history
    `${USER_URL}/api/account-configs/${accountId}/sftp-config/export-history/schedule/${scheduleId}`,
    {
      params: {
        page,
        size
      },
      headers: { Authorization: `Bearer ${tokenManager.getAccessToken()}` }
    }
  );
  return response.data;
};

/**
 * Get export statistics
 * GET /api/sftp/export-statistics?accountId={accountId}
 * @param {string} accountId - The account ID
 * @returns {Promise} API response
 */
export const getExportStatistics = async (accountId) => {
  const response = await axios.get(`${USER_URL}/api/sftp/export-statistics`, {
    params: { accountId },
    headers: { Authorization: `Bearer ${tokenManager.getAccessToken()}` }
  });
  return response.data;
};

// Export all functions as default object as well
const connectorService = {
  // SFTP Configurations
  getAllConfigurations,
  getConfigurationsByType,
  getConfigurationById,
  createConfiguration,
  updateConfiguration,
  deleteConfiguration,
  testConnection,
  
  // Export Schedules
  getAllSchedules,
  getScheduleById,
  createSchedule,
  updateSchedule,
  toggleSchedule,
  executeScheduleNow,
  deleteSchedule,
  
  // Export History & Statistics
  getExportHistory,
  getScheduleExportHistory,
  getExportStatistics,
};

export default connectorService;